/**
 * InsightCard — compact card rendered inside InsightsPanel for a single InsightNode.
 *
 * Visually distinct from OST tree nodes:
 *  - No type-header label at the top
 *  - Left and right connection port affordances (hidden via CSS, kept in DOM for future use)
 *  - Drag handle on the left for vertical reordering within the panel
 *
 * Description view supports rich text selection:
 *  - Existing InsightLinks are highlighted yellow with a drag-to-connect affordance
 *  - After selecting new text (native browser selection + mouseup), the selection is
 *    pinned as a pending highlight with the same drag-to-connect affordance
 *  - Dragging from any highlight initiates an insight-link drag gesture that
 *    Canvas.tsx handles to connect the snippet to an opportunity node
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { InsightNode, InsightLink } from 'types';
import { DEFAULT_INSIGHT_CORNER_COLOR } from '../ui/InsightForm';
import { DragHandle } from 'components/ui';

// InsightCard layout and UX constants.

/** Height of the card in its collapsed (display) state. */
export const INSIGHT_CARD_H = 56;

// Data model types used by InsightCard.

/** A text selection captured from the description area that has not yet been linked. */
interface PendingSelection {
    text: string;
    start: number;
    end: number;
}

/** Info passed back to the parent when the user begins dragging a highlighted snippet. */
export interface InsightDragInfo {
    insightId: string;
    text: string;
    start: number;
    end: number;
    /** ID of an existing InsightLink being re-dragged; null when creating a new link. */
    linkId: string | null;
}

type InsightCardProps = {
    insight: InsightNode;
    /** InsightLinks that belong to this insight card (pre-filtered by insightId). */
    insightLinks: InsightLink[];
    /** Mouse-down handler passed from the parent for drag-to-reorder. */
    onDragHandleMouseDown: (e: React.MouseEvent, id: string) => void;
    /** Called when the user starts dragging a highlighted text snippet to connect it to an opportunity. */
    onDragFromLink: (e: React.MouseEvent, info: InsightDragInfo) => void;
};

// Highlight rendering helpers

interface HighlightSegment {
    text: string;
    linkId: string | null;
    start: number;
    end: number;
}

/**
 * Breaks the description string into alternating plain-text and highlighted segments
 * based on confirmed InsightLinks and the current pending selection. Overlapping
 * ranges are resolved by truncation (first range wins).
 */
export function buildDescriptionSegments(
    description: string = '',
    pendingSelection: PendingSelection | null,
    insightLinks: InsightLink[],
): HighlightSegment[] {
    type RangeEntry = { start: number; end: number; linkId: string | null };

    const raw: RangeEntry[] = [
        ...insightLinks.map((l) => ({ start: l.selectionStart, end: l.selectionEnd, linkId: l.id })),
    ];

    if (pendingSelection) {
        raw.push({ start: pendingSelection.start, end: pendingSelection.end, linkId: null });
    }

    // Clamp to valid description bounds and drop degenerate ranges
    const bounded = raw
        .map((r) => ({ ...r, start: Math.max(0, r.start), end: Math.min(description.length, r.end) }))
        .filter((r) => r.start < r.end);

    // Sort by start, merge overlaps by truncating later ranges
    bounded.sort((a, b) => a.start - b.start);
    const merged: RangeEntry[] = [];
    let cursor = 0;
    for (const r of bounded) {
        if (r.start >= cursor) {
            merged.push(r);
            cursor = r.end;
        }
    }

    // Build segments list
    const segments: HighlightSegment[] = [];
    cursor = 0;
    for (const r of merged) {
        if (r.start > cursor) {
            // Plain text before this highlight
            segments.push({ text: description.slice(cursor, r.start), linkId: null, start: cursor, end: r.start });
        }
        segments.push({ text: description.slice(r.start, r.end), linkId: r.linkId, start: r.start, end: r.end });
        cursor = r.end;
    }
    if (cursor < description.length) {
        segments.push({ text: description.slice(cursor), linkId: null, start: cursor, end: description.length });
    }
    return segments;
}

export default function InsightCard({
    insight,
    insightLinks,
    onDragHandleMouseDown,
    onDragFromLink,
}: InsightCardProps) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [expandedCardHeight, setExpandedCardHeight] = useState(INSIGHT_CARD_H);

    useEffect(() => {
        if (!isDescriptionExpanded) return;
        const nextHeight = cardRef.current?.scrollHeight ?? INSIGHT_CARD_H;
        setExpandedCardHeight(Math.ceil(nextHeight));
    }, [isDescriptionExpanded, insight.title, insight.description, insight.cornerColor]);

    useEffect(() => {
        if (!isDescriptionExpanded) return;

        const handleOutsidePointerDown = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (cardRef.current && !cardRef.current.contains(target)) {
                setIsDescriptionExpanded(false);
                setExpandedCardHeight(INSIGHT_CARD_H);
            }
        };

        document.addEventListener('mousedown', handleOutsidePointerDown);

        return () => {
            document.removeEventListener('mousedown', handleOutsidePointerDown);
        };
    }, [isDescriptionExpanded]);

    /** Text the user has selected but not yet dragged to an opportunity. */
    const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);


    /** Ref to the description display div for computing character offsets from Range objects. */
    const descRef = useRef<HTMLDivElement>(null);

    /* Removed modifier key tracking — highlights now initiate drag without Ctrl/Cmd. */

    /** Inline cursor value for highlighted segments — uses PUBLIC_URL so webpack doesn't try to resolve it. */
    const pinchCursor = `url(${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur) 16 16, crosshair`;

    /** Custom grab cursor for the drag handle — uses PUBLIC_URL so webpack doesn't try to resolve it. */
    const grabCursor = `url(${process.env.PUBLIC_URL || ''}/hand-cursor.cur) 8 8, grab`;

    /**
     * Detects a completed text selection in the description div. Captures the selected
     * text and its character offsets, then stores them as a pending selection.
     */
    const handleDescriptionMouseUp = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !descRef.current) return;

        const selectedText = sel.toString();
        // Ignore micro-selections (single characters and whitespace-only selections).
        if (!selectedText.trim() || selectedText.trim().length < 2) return;

        const range = sel.getRangeAt(0);
        const container = descRef.current;

        // Ensure the selection is actually within our description div
        if (!container.contains(range.commonAncestorContainer)) return;

        // Compute character offset from start of container to start of selection
        const preRange = range.cloneRange();
        preRange.selectNodeContents(container);
        preRange.setEnd(range.startContainer, range.startOffset);
        const selectionStart = preRange.toString().length;
        const selectionEnd = selectionStart + selectedText.length;

        setPendingSelection({ text: selectedText, start: selectionStart, end: selectionEnd });
    }, []);

    /**
     * Handles mousedown on the description area. Intercepts clicks on highlighted
     * segments to begin a drag-to-connect gesture; clears the pending selection
     * when the user starts a fresh text selection.
     */
    const handleDescriptionMouseDown = useCallback(
        (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            const markEl = target.closest<HTMLElement>('.insight-highlight');

            // If user clicks on a highlighted segment, start the drag-connect gesture
            // immediately (no Ctrl/Cmd required) and prevent the browser from
            // starting a native text selection.
            if (markEl) {
                e.preventDefault();
                const linkId = markEl.dataset.linkId || null;
                const start = parseInt(markEl.dataset.selStart ?? '0', 10);
                const end = parseInt(markEl.dataset.selEnd ?? '0', 10);
                const text = markEl.dataset.customText ?? markEl.textContent ?? '';

                onDragFromLink(e, { insightId: insight.id, text, start, end, linkId });
                return;
            }

            // Clear any pending selection when the user starts a fresh one.
            setPendingSelection(null);
        },
        [insight.id, onDragFromLink],
    );

    const renderDisplayCard = () => {
        const segments = buildDescriptionSegments(insight.description, pendingSelection, insightLinks);

        return (
            <div
                ref={cardRef}
                className='insight-card'
                style={{ '--card-height': isDescriptionExpanded ? `${expandedCardHeight}px` : `${INSIGHT_CARD_H}px` } as React.CSSProperties}
                onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('.insight-description-view') || target.closest('.insight-drag-handle')) return;
                    const nextHeight = cardRef.current?.scrollHeight ?? INSIGHT_CARD_H;
                    setExpandedCardHeight(Math.ceil(nextHeight));
                    setIsDescriptionExpanded((v) => !v);
                }}
            >
                <span
                    className='insight-card-corner'
                    aria-hidden='true'
                    style={{ '--corner-color': insight.cornerColor || DEFAULT_INSIGHT_CORNER_COLOR } as React.CSSProperties}
                />

                {/* Left connection port affordance — hidden via CSS, kept in DOM */}
                <span className='insight-port insight-port--left' title='Connection port' />

                {/* Drag handle for vertical reordering within the panel */}
                <DragHandle
                    className='insight-drag-handle'
                    title='Drag to reorder'
                    style={{ cursor: grabCursor }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        onDragHandleMouseDown(e, insight.id);
                    }}
                />

                {/* Card content */}
                <div className='insight-card-body'>
                    <p
                        className='insight-card-title'
                        role='button'
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const nextHeight = cardRef.current?.scrollHeight ?? INSIGHT_CARD_H;
                                setExpandedCardHeight(Math.ceil(nextHeight));
                                setIsDescriptionExpanded((v) => !v);
                            }
                        }}
                    >
                        {insight.title}
                    </p>
                    <div className='insight-card-meta'>
                        {insight.description && (
                            <div
                                ref={descRef}
                                className={`insight-description-view ${isDescriptionExpanded ? 'insight-description-view--expanded' : ''}`}
                                title={insight.description}
                                onMouseUp={handleDescriptionMouseUp}
                                onMouseDown={handleDescriptionMouseDown}
                                // Block card's onDoubleClick from firing when user double-clicks to select text
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isDescriptionExpanded) {
                                        const nextHeight = cardRef.current?.scrollHeight ?? INSIGHT_CARD_H;
                                        setExpandedCardHeight(Math.ceil(nextHeight));
                                        setIsDescriptionExpanded(true);
                                    }
                                }}
                                onDoubleClick={(e) => e.stopPropagation()}
                            >
                                {segments.length === 0 ? (
                                    insight.description
                                ) : (
                                    segments.map((seg, i) =>
                                        seg.linkId !== null || (pendingSelection && seg.start === pendingSelection.start) ? (
                                            // Highlighted segment — draggable to connect to an opportunity
                                            <mark
                                                key={i}
                                                className={`insight-highlight ${seg.linkId ? 'insight-highlight--confirmed' : 'insight-highlight--pending'}`}
                                                data-link-id={seg.linkId ?? ''}
                                                data-sel-start={String(seg.start)}
                                                data-sel-end={String(seg.end)}
                                                data-custom-text={seg.text}
                                                title='Drag to connect to an opportunity'
                                                style={{ cursor: pinchCursor }}
                                            >
                                                {seg.text}
                                            </mark>
                                        ) : (
                                            // Plain text segment
                                            <span key={i}>{seg.text}</span>
                                        ),
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right connection port affordance — hidden via CSS, kept in DOM */}
                <span className='insight-port insight-port--right' title='Connection port' />
            </div>
        );
    };

    return renderDisplayCard();
}
